import React, { useEffect } from 'react';
import {
    ImageOff,
    Type,
    Wind,
    Layers,
    Crown,
    PersonStanding,
    Sparkles,
    Film,
    Activity,
    FlaskConical,
    MessageSquare,
    CalendarClock,
    FolderUp,
    ClipboardCheck,
    Timer,
    ScrollText,
    Gift,
    Clapperboard,
    Captions,
    ShieldCheck,
    CheckCircle2,
    XCircle
} from 'lucide-react';

/* Reusable card: icon + heading + subheading, nothing else */
const InfoCard = ({ icon: Icon, heading, subheading, accent = "indigo" }) => {
    const accents = {
        indigo: {
            bg: "bg-indigo-900/10",
            border: "border-indigo-500/30",
            iconBg: "bg-indigo-500/10",
            iconColor: "text-indigo-300"
        },
        rose: {
            bg: "bg-rose-900/10",
            border: "border-rose-500/30",
            iconBg: "bg-rose-500/10",
            iconColor: "text-rose-300"
        },
        emerald: {
            bg: "bg-emerald-900/10",
            border: "border-emerald-500/30",
            iconBg: "bg-emerald-500/10",
            iconColor: "text-emerald-300"
        },
        amber: {
            bg: "bg-amber-900/10",
            border: "border-amber-500/30",
            iconBg: "bg-amber-500/10",
            iconColor: "text-amber-300"
        }
    };
    const a = accents[accent];
    return (
        <div className={`rounded-2xl border ${a.border} ${a.bg} p-6 sm:p-7 flex flex-col`}>
            <div className={`${a.iconBg} ${a.iconColor} w-11 h-11 rounded-xl flex items-center justify-center mb-5`}>
                <Icon className="w-5 h-5" />
            </div>
            <h3 className="text-base sm:text-lg font-black text-white mb-2 leading-snug">{heading}</h3>
            <p className={`text-xs sm:text-sm font-medium leading-snug ${accent === 'rose' ? 'text-rose-100/70' : 'text-white/55'}`}>{subheading}</p>
        </div>
    );
};

const SectionEyebrow = ({ children, color = "indigo" }) => {
    const colors = {
        indigo: "text-indigo-300/80",
        rose: "text-rose-300/80",
        amber: "text-amber-300/80",
        emerald: "text-emerald-300/80"
    };
    return (
        <div className={`text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-4 ${colors[color]}`}>
            {children}
        </div>
    );
};

export default function AbhiveerCourtReady() {

    useEffect(() => {
        document.title = "COURT READY | Course Production";
        window.scrollTo(0, 0);
    }, []);

    const videos = [
        { n: '01', h: 'Phase 1 · Foam Rolling', s: '15 release points, plantar fascia to posterior shoulder, each with the working muscle lit on screen.' },
        { n: '02', h: 'Phase 2 · Mobility', s: 'Every movement shown the way you coach it, not the way stock footage approximates it.' },
        { n: '03', h: 'Phase 3 · Activation', s: 'Upper and lower body activation with the target muscle highlighted on every exercise.' },
        { n: '04', h: 'Phase 4 · Neuromuscular Priming', s: 'The longest and most technical video, carried by the science graphics instead of wallpaper B-roll.' },
        { n: '05', h: 'Full Routine · Real-Time Follow-Along', s: 'The flagship. A player presses play, follows you through all four phases, and finishes court ready.' }
    ];

    const timeline = [
        { n: 'Day 0', h: 'Handover', s: 'Your raw files and final scripts land in the shared Drive folder. The clock starts here.' },
        { n: 'Day 3', h: 'Visual System locked', s: 'Character, muscle-highlight look, and caption style signed off on one sample section. One approval, then we run.' },
        { n: 'Day 7', h: 'Video 1 on Frame.io', s: 'Foam Rolling, first cut, ready for your timestamped comments.' },
        { n: 'Day 21', h: 'All five videos final', s: 'Launch-ready course, plus the bonus launch reels cut and delivered.' }
    ];

    const wont = [
        { h: 'A launch date we cannot control', s: 'The Day-21 date holds when raw files and approvals arrive on time. Delays on either side shift it by the same number of days.' },
        { h: 'Photorealistic fake humans', s: 'Realistic AI people read as fake and damage trust. The stylized character is deliberate: players know it is a model and still understand every cue.' },
        { h: 'Course sales numbers', s: 'We make the course look like it costs what it costs. Selling it is your funnel; we hand you the launch reels to feed it.' }
    ];

    const will = [
        { h: 'Every video matches the sample you approved', s: 'That standard is the contract. If a video misses it, we keep editing free until it hits.' },
        { h: 'A visual system you own forever', s: 'Character, muscle-highlight language, caption kit. Every future COURT READY video starts from it at no extra design cost.' },
        { h: 'Two revision rounds per video on Frame.io', s: 'Same review flow you already use. Timestamped comments, fast turnarounds.' },
        { h: 'First video in your hands at Day 7', s: 'You see the real thing inside a week, not a promise at the end.' }
    ];

    return (
        <div className="font-sans leading-relaxed text-white overflow-x-hidden selection:bg-indigo-500/30 pt-36 sm:pt-40 pb-20 relative z-10">

            {/* Floating Header */}
            <header className="fixed top-0 left-0 right-0 z-50 p-4 sm:p-6 flex justify-between items-center bg-transparent pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-3 sm:px-4 py-2 rounded-full pointer-events-auto shadow-lg">
                    <span className="font-bold tracking-tight text-white text-sm sm:text-base">Social<span className="text-indigo-400">SEO</span></span>
                    <span className="text-white/30 mx-1.5 sm:mx-2">/</span>
                    <span className="text-white/70 font-medium text-xs sm:text-sm">COURT READY</span>
                </div>
                <div className="bg-indigo-500/20 backdrop-blur-lg border border-indigo-500/30 px-2.5 sm:px-3 py-1 rounded-full pointer-events-auto">
                    <span className="text-indigo-300 text-[10px] sm:text-xs font-bold tracking-widest uppercase">Private</span>
                </div>
            </header>

            <main className="max-w-[1080px] mx-auto px-6 space-y-28 sm:space-y-40">

                {/* HERO */}
                <section className="text-center pt-12 sm:pt-20">
                    <div className="text-indigo-300/70 text-[10px] sm:text-xs font-bold tracking-[0.3em] uppercase mb-10">
                        Confidential · Private Proposal · Abhiveer Shekhawat
                    </div>

                    <h1 className="text-5xl sm:text-6xl md:text-7xl font-black tracking-tight mb-8 bg-clip-text text-transparent bg-gradient-to-r from-white via-indigo-200 to-indigo-400 leading-[1.05] pb-2">
                        Your course is filmed.<br />Now make it premium.
                    </h1>

                    <p className="text-base sm:text-lg md:text-xl text-white/55 max-w-2xl mx-auto leading-relaxed mb-16 font-light">
                        COURT READY is scripted word for word and recorded. What is missing is the visual layer you already approved in the sample:<br className="hidden sm:block" /> a locked 3D character performing every movement with the working muscle lit up on screen.
                    </p>

                    <div className="max-w-md mx-auto">
                        <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm px-6 py-8">
                            <div className="text-[10px] sm:text-xs text-white/40 font-bold tracking-widest uppercase mb-3">Investment · One-time</div>
                            <div className="text-4xl sm:text-5xl font-black text-white tracking-tight">INR 1,50,000</div>
                            <div className="text-xs sm:text-sm text-white/50 mt-2">Plus GST · 100% upfront · 5 course videos + 3 launch reels + the Visual System</div>
                        </div>
                    </div>
                </section>

                {/* PAIN POINTS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="rose">The honest read</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Why the current edit<br />undersells your course.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={ImageOff} heading="Stock footage says nothing" subheading="Every stock tennis clip is the same player hitting the same stroke. Your cues are more specific than that." accent="rose" />
                        <InfoCard icon={Type} heading="Captions vanish on your shirt" subheading="White text on a white shirt. A player following along cannot read the cue that matters." accent="rose" />
                        <InfoCard icon={Wind} heading="Breathing cues cannot be stocked" subheading="Inhale four seconds, exhale open-mouth. No stock library shows that. A prompted character does." accent="rose" />
                        <InfoCard icon={Layers} heading="The science reads as wallpaper" subheading="Each phase has a SCIENCE block. Generic B-roll under it wastes your strongest credibility moment." accent="rose" />
                        <InfoCard icon={Crown} heading="Premium course, commodity edit" subheading="A certified iTPA and CTPS system deserves visuals no other tennis course has." accent="rose" />
                        <InfoCard icon={PersonStanding} heading="No visual identity to reuse" subheading="Every future video starts from zero unless the course locks a system now." accent="rose" />
                    </div>
                </section>

                {/* THE FIVE VIDEOS */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The deliverable</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Five videos.<br />One complete system.
                        </h2>
                        <p className="text-base sm:text-lg text-white/55 max-w-2xl mx-auto mt-6 font-light leading-relaxed">
                            Roughly 60 to 90 minutes of finished course content. Your talking head, your exercise footage, and the AI visual layer on every minute of it.
                        </p>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {videos.map((v) => (
                            <div key={v.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-2xl sm:text-3xl font-black text-indigo-400/80 shrink-0 w-10 sm:w-12 tabular-nums">{v.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{v.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{v.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* WHAT WE BUILD */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">Our side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What goes into every video.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Sparkles} heading="The COURT READY Visual System" subheading="One locked character, one design language, built once and reused across all five videos and everything after" accent="indigo" />
                        <InfoCard icon={Activity} heading="Muscle-highlight overlays" subheading="Foam rolling the hamstring? The hamstring lights up. Every exercise, every phase" accent="indigo" />
                        <InfoCard icon={Film} heading="AI inserts where stock fails" subheading="Breathing, niche movement patterns, transitions, generated in the approved style, never generic clips" accent="indigo" />
                        <InfoCard icon={FlaskConical} heading="Science-section graphics" subheading="Your SCIENCE blocks visualized with motion graphics that make the certification visible" accent="indigo" />
                        <InfoCard icon={Captions} heading="Readable caption system" subheading="Contrast-first caption design tested against your actual footage before Video 1 ships" accent="indigo" />
                        <InfoCard icon={MessageSquare} heading="Frame.io review flow" subheading="Two revision rounds per video, timestamped comments, the flow you already know" accent="indigo" />
                    </div>
                </section>

                {/* TIMELINE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="indigo">The clock</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            21 days from handover.
                        </h2>
                    </div>

                    <div className="max-w-3xl mx-auto space-y-4">
                        {timeline.map((t) => (
                            <div key={t.n} className="flex items-start gap-5 sm:gap-7 rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm p-5 sm:p-6">
                                <div className="text-lg sm:text-xl font-black text-indigo-400/80 shrink-0 w-16 sm:w-20">{t.n}</div>
                                <div>
                                    <h3 className="text-base sm:text-lg font-black text-white mb-1 leading-snug">{t.h}</h3>
                                    <p className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{t.s}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* BONUSES */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">Included, on top</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Three bonuses,<br />INR 85,000 of them.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={Gift} heading="Visual System ownership · INR 40,000" subheading="The character and design files are yours. Every future video starts from them at no extra design cost" accent="amber" />
                        <InfoCard icon={Clapperboard} heading="Launch Cut Pack · INR 30,000" subheading="Three vertical promo reels cut from the course footage, ready to sell the course on launch day" accent="amber" />
                        <InfoCard icon={ScrollText} heading="Caption + Brand Kit · INR 15,000" subheading="The contrast-fixed caption template packaged so any future editor keeps the standard" accent="amber" />
                    </div>
                </section>

                {/* YOUR SIDE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="emerald">Your side</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            Four things, then we run.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5 max-w-5xl mx-auto">
                        <InfoCard icon={FolderUp} heading="Raw files handover" subheading="Talking heads and exercise recordings into the shared Drive folder" accent="emerald" />
                        <InfoCard icon={ScrollText} heading="Final scripts" subheading="The word-for-word filming scripts, frozen before editing starts" accent="emerald" />
                        <InfoCard icon={ClipboardCheck} heading="Day-3 sign-off" subheading="One approval on the Visual System sample section" accent="emerald" />
                        <InfoCard icon={Timer} heading="48-hour comments" subheading="Frame.io feedback within two days of each cut keeps Day 21 honest" accent="emerald" />
                    </div>
                </section>

                {/* PROMISE */}
                <section>
                    <div className="text-center mb-12 sm:mb-16">
                        <SectionEyebrow color="amber">The guarantee</SectionEyebrow>
                        <h2 className="text-3xl sm:text-4xl md:text-5xl font-black tracking-tight text-white leading-tight">
                            What we promise.<br />And what we won't.
                        </h2>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 max-w-5xl mx-auto">
                        {/* WON'T */}
                        <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-7 sm:p-9">
                            <div className="text-rose-300/80 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we won't promise</div>
                            <ul className="space-y-6">
                                {wont.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <XCircle className="w-5 h-5 text-rose-400/70 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/50 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>

                        {/* WILL */}
                        <div className="rounded-3xl border border-indigo-500/30 bg-indigo-900/10 p-7 sm:p-9">
                            <div className="text-indigo-300/90 text-[10px] sm:text-xs font-bold tracking-[0.25em] uppercase mb-7">What we do promise</div>
                            <ul className="space-y-6">
                                {will.map((item) => (
                                    <li key={item.h} className="flex items-start gap-3.5">
                                        <CheckCircle2 className="w-5 h-5 text-indigo-300 shrink-0 mt-0.5" />
                                        <div>
                                            <div className="text-sm sm:text-base font-bold text-white leading-snug mb-1">{item.h}</div>
                                            <div className="text-xs sm:text-sm text-white/55 font-medium leading-snug">{item.s}</div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>

                    <div className="max-w-3xl mx-auto mt-10">
                        <div className="rounded-2xl border border-amber-500/30 bg-amber-900/10 p-6 sm:p-8 flex items-start gap-4">
                            <ShieldCheck className="w-6 h-6 text-amber-300 shrink-0 mt-1" />
                            <p className="text-sm sm:text-base text-amber-100/80 font-medium leading-relaxed">
                                If any of the five videos does not match the visual standard of the sample you approved, we keep editing it free until it does. Condition: raw files and final scripts arrive before editing starts; new footage or script rewrites after a first cut count as a new revision round.
                            </p>
                        </div>
                    </div>
                </section>

                {/* FINAL CTA */}
                <section className="text-center pt-4 sm:pt-8">
                    <h2 className="text-3xl sm:text-4xl font-black tracking-tight text-white leading-tight mb-6">
                        Say go on WhatsApp.
                    </h2>
                    <p className="text-white/55 text-sm sm:text-base font-light max-w-xl mx-auto">
                        We send the payment link and the Drive folder structure the same day. The Day-0 clock starts the moment your raw files land, and Video 1 is on Frame.io a week later.
                    </p>
                </section>

                {/* FOOTER */}
                <footer className="text-center text-slate-500 text-xs sm:text-sm pt-20 border-t border-white/5">
                    <div className="font-bold text-white/60 mb-3 tracking-wider">SocialSEO LLP</div>
                    <div>
                        <a href="mailto:suresh@socialseo.in" className="text-indigo-300/80 hover:text-indigo-200 transition">suresh@socialseo.in</a>
                    </div>
                </footer>

            </main>
        </div>
    );
}
